// src/hooks/useAdvancedSearch.js
/**
 * Custom hook for advanced search functionality
 * Separates search logic from UI component to reduce complexity
 */
import { useState, useCallback, useRef } from "react";
import { advancedRecommendationEngine } from "../services/advancedRecommendationEngine";

export const useAdvancedSearch = (inventory = [], maxResults = 10) => {
  const [searchState, setSearchState] = useState({
    query: "",
    results: null,
    loading: false,
    error: null,
    selectedResult: null,
  });

  const [filters, setFilters] = useState({
    priceRange: { min: "", max: "" },
    categories: [],
    occasions: [],
    regions: [],
  });

  const searchTimeoutRef = useRef(null);

  const updateSearchState = useCallback((updates) => {
    setSearchState((prev) => ({ ...prev, ...updates }));
  }, []);

  const buildSearchContext = useCallback(
    (currentRestaurant) => {
      return {
        priceRange:
          filters.priceRange.min || filters.priceRange.max
            ? {
                min: parseFloat(filters.priceRange.min) || 0,
                max: parseFloat(filters.priceRange.max) || Infinity,
              }
            : null,
        categories: filters.categories,
        occasions: filters.occasions,
        regions: filters.regions,
        restaurant: currentRestaurant?.name,
        maxResults,
      };
    },
    [filters, maxResults]
  );

  const performSearch = useCallback(
    async (searchQuery, currentRestaurant) => {
      if (!searchQuery.trim()) {
        updateSearchState({ results: null, error: null });
        return null;
      }

      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      updateSearchState({ loading: true, error: null });

      try {
        const searchContext = buildSearchContext(currentRestaurant);

        // Ensure the method exists and is callable
        if (
          !advancedRecommendationEngine ||
          typeof advancedRecommendationEngine.naturalLanguageSearch !==
            "function"
        ) {
          throw new Error("Advanced recommendation engine not available");
        }

        const results = await Promise.resolve(
          advancedRecommendationEngine.naturalLanguageSearch(
            searchQuery,
            inventory,
            searchContext
          )
        );

        updateSearchState({
          results,
          loading: false,
          query: searchQuery,
        });

        return results;
      } catch (err) {
        console.error("Search error:", err);

        // Fallback to simple search
        const fallbackResults = {
          query: searchQuery,
          results: inventory
            .filter(
              (item) =>
                item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.category?.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .slice(0, maxResults),
          explanation: `Basic search results for "${searchQuery}". Advanced AI search temporarily unavailable.`,
          fallback: true,
        };

        updateSearchState({
          results: fallbackResults,
          loading: false,
          query: searchQuery,
          error: null,
        });

        return fallbackResults;
      }
    },
    [inventory, updateSearchState, buildSearchContext, maxResults]
  );

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      priceRange: { min: "", max: "" },
      categories: [],
      occasions: [],
      regions: [],
    });
  }, []);

  const selectResult = useCallback(
    (result) => {
      updateSearchState({ selectedResult: result });
    },
    [updateSearchState]
  );

  const setQuery = useCallback(
    (query) => {
      updateSearchState({ query });
    },
    [updateSearchState]
  );

  const getFilterCount = useCallback(() => {
    let count = 0;
    if (filters.priceRange.min || filters.priceRange.max) count++;
    if (filters.categories.length > 0) count++;
    if (filters.occasions.length > 0) count++;
    if (filters.regions.length > 0) count++;
    return count;
  }, [filters]);

  return {
    // State
    ...searchState,
    filters,

    // Actions
    performSearch,
    handleFilterChange,
    clearFilters,
    selectResult,
    setQuery,
    getFilterCount,
  };
};
